import { useState, useCallback, useEffect, useRef } from 'react';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import { useContacts, type Contact } from '@/app/client-zone/modules/total-finance/hooks/useContacts';
import type { ContactSearchResult } from '@/components/shared/ContactLocationSearch';
import type { DocumentFormState } from '../documentModalHelpers';

export interface LocationResult {
  id: number;
  contact_id: number;
  location_name: string;
  location_type?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  is_primary?: boolean;
}

export interface ContactPerson {
  id: number;
  contact_id: number;
  first_name: string;
  last_name: string;
  job_title?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  is_primary?: boolean;
}

export interface UnifiedSearchResult {
  type: 'contact' | 'location' | 'person';
  id: number;
  contact_id: number;
  contact_name: string;
  contact_company_name: string | null;
  contact_type: string;
  location_id: number | null;
  location_name: string | null;
  location_address: string | null;
  location_city: string | null;
  person_id: number | null;
  person_name: string | null;
  person_job_title: string | null;
  person_email: string | null;
  person_phone: string | null;
  display_title: string;
  display_subtitle: string;
}

interface UseContactSearchParams {
  isPurchaseDocument: boolean;
  isOpen: boolean;
  formData: DocumentFormState;
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormState>>;
  insertButtonRef: React.RefObject<HTMLButtonElement | null>;
}

export function useContactSearch({
  isPurchaseDocument,
  isOpen,
  formData,
  setFormData,
  insertButtonRef,
}: UseContactSearchParams) {
  const { contacts, loadContacts } = useContacts();

  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [locations, setLocations] = useState<LocationResult[]>([]);
  const [contactPeople, setContactPeople] = useState<ContactPerson[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<LocationResult | null>(null);
  const [selectedContactPerson, setSelectedContactPerson] = useState<ContactPerson | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<LocationResult | null>(null);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingContactPeople, setLoadingContactPeople] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactSearchMode, setContactSearchMode] = useState<'contact' | 'location' | 'person'>('contact');

  const [contactSearch, setContactSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [personSearch, setPersonSearch] = useState('');
  const [locationSearchResults, setLocationSearchResults] = useState<UnifiedSearchResult[]>([]);
  const [personSearchResults, setPersonSearchResults] = useState<UnifiedSearchResult[]>([]);
  const [loadingLocationSearch, setLoadingLocationSearch] = useState(false);
  const [loadingPersonSearch, setLoadingPersonSearch] = useState(false);
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);
  const [showStakeholderModal, setShowStakeholderModal] = useState(false);
  const [isLocationSearchOpen, setIsLocationSearchOpen] = useState(false);
  const [isPersonSearchOpen, setIsPersonSearchOpen] = useState(false);
  const [highlightedContactIndex, setHighlightedContactIndex] = useState(-1);

  const [showAddLocation, setShowAddLocation] = useState(false);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [savingPerson, setSavingPerson] = useState(false);
  const [newLocation, setNewLocation] = useState({
    location_name: '',
    location_type: 'office',
    address_line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'South Africa',
    is_primary: false,
  });
  const [newPerson, setNewPerson] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    job_title: '',
    is_primary: false,
  });

  const contactDropdownRef = useRef<HTMLDivElement>(null);
  const locationSearchRef = useRef<HTMLDivElement>(null);
  const personSearchRef = useRef<HTMLDivElement>(null);
  const contactSearchInputRef = useRef<HTMLInputElement>(null);
  const activeContactIdRef = useRef<number | null>(null);
  const contactHydrateFromIdRequestRef = useRef(0);
  const latestLocationsLoadRequestId = useRef(0);
  const latestContactPeopleLoadRequestId = useRef(0);
  const latestLocationSearchRequestId = useRef(0);
  const latestPersonSearchRequestId = useRef(0);

  const loadLocationsForContact = useCallback(async (contactId: number) => {
    if (!contactId) {
      latestLocationsLoadRequestId.current += 1;
      setLocations([]);
      setLoadingLocations(false);
      return;
    }

    activeContactIdRef.current = contactId;
    const requestId = ++latestLocationsLoadRequestId.current;
    setLoadingLocations(true);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/contacts/${contactId}/locations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (requestId === latestLocationsLoadRequestId.current && Number(activeContactIdRef.current) === Number(contactId)) {
          setLocations([]);
        }
        return;
      }

      const data = await response.json();
      if (requestId !== latestLocationsLoadRequestId.current || Number(activeContactIdRef.current) !== Number(contactId)) {
        return;
      }

      setLocations(data.data || data.locations || []);
    } catch (error) {
      if (requestId !== latestLocationsLoadRequestId.current || Number(activeContactIdRef.current) !== Number(contactId)) {
        return;
      }

      console.error('Error loading locations:', error);
      setLocations([]);
    } finally {
      if (requestId === latestLocationsLoadRequestId.current && Number(activeContactIdRef.current) === Number(contactId)) {
        setLoadingLocations(false);
      }
    }
  }, []);

  const loadContactPeopleForContact = useCallback(async (contactId: number) => {
    if (!contactId) {
      latestContactPeopleLoadRequestId.current += 1;
      setContactPeople([]);
      setLoadingContactPeople(false);
      return;
    }

    activeContactIdRef.current = contactId;
    const requestId = ++latestContactPeopleLoadRequestId.current;
    setLoadingContactPeople(true);
    try {
      const { fetchContactPeople } = await import('@/lib/contacts/fetchContactPeople');
      const token = authService.getTokens()?.access_token;
      if (requestId !== latestContactPeopleLoadRequestId.current || Number(activeContactIdRef.current) !== Number(contactId)) {
        return;
      }

      const people = await fetchContactPeople<ContactPerson>({
        contactId,
        token,
      });

      setContactPeople(people);
    } catch (error) {
      if (requestId !== latestContactPeopleLoadRequestId.current || Number(activeContactIdRef.current) !== Number(contactId)) {
        return;
      }

      console.error('Error loading contact people:', error);
      setContactPeople([]);
    } finally {
      if (requestId === latestContactPeopleLoadRequestId.current && Number(activeContactIdRef.current) === Number(contactId)) {
        setLoadingContactPeople(false);
      }
    }
  }, []);

  const performLocationSearch = useCallback(async (search: string) => {
    const normalizedSearch = search.trim();
    if (normalizedSearch.length < 2) {
      latestLocationSearchRequestId.current += 1;
      setLocationSearchResults([]);
      setLoadingLocationSearch(false);
      return;
    }

    const requestId = ++latestLocationSearchRequestId.current;
    setLoadingLocationSearch(true);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(
        `/api/v1/contacts/locations/search?search=${encodeURIComponent(normalizedSearch)}&limit=30`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (requestId !== latestLocationSearchRequestId.current) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const results: UnifiedSearchResult[] = (data.data || []).map((loc: Record<string, unknown>) => ({
          type: 'location' as const,
          id: loc.id as number,
          contact_id: loc.contact_id as number,
          contact_name: (loc.contact_name as string) || '',
          contact_company_name: (loc.contact_company as string) || null,
          contact_type: '',
          location_id: loc.id as number,
          location_name: (loc.location_name as string) || '',
          location_address: [loc.address_line1, loc.address_line2].filter(Boolean).join(', ') || null,
          location_city: (loc.city as string) || null,
          person_id: null,
          person_name: null,
          person_job_title: null,
          person_email: null,
          person_phone: null,
          display_title: (loc.location_name as string) || '',
          display_subtitle: [loc.address_line1, loc.city].filter(Boolean).join(', ') || '',
        }));
        setLocationSearchResults(results);
      } else {
        setLocationSearchResults([]);
      }
    } catch (error) {
      if (requestId !== latestLocationSearchRequestId.current) {
        return;
      }

      console.error('Error in location search:', error);
      setLocationSearchResults([]);
    } finally {
      if (requestId === latestLocationSearchRequestId.current) {
        setLoadingLocationSearch(false);
      }
    }
  }, []);

  const performPersonSearch = useCallback(async (search: string) => {
    const normalizedSearch = search.trim();
    if (normalizedSearch.length < 2) {
      latestPersonSearchRequestId.current += 1;
      setPersonSearchResults([]);
      setLoadingPersonSearch(false);
      return;
    }

    const requestId = ++latestPersonSearchRequestId.current;
    setLoadingPersonSearch(true);
    try {
      const token = authService.getTokens()?.access_token;
      const contactTypes = isPurchaseDocument ? 'supplier' : 'customer';
      const response = await fetch(
        `/api/v1/contacts/search/unified/?search=${encodeURIComponent(normalizedSearch)}&contact_types=${contactTypes}&types=person&limit=30`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (requestId !== latestPersonSearchRequestId.current) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const people = (data.data || []).filter((r: UnifiedSearchResult) => r.type === 'person');
        setPersonSearchResults(people);
      } else {
        setPersonSearchResults([]);
      }
    } catch (error) {
      if (requestId !== latestPersonSearchRequestId.current) {
        return;
      }

      console.error('Error in person search:', error);
      setPersonSearchResults([]);
    } finally {
      if (requestId === latestPersonSearchRequestId.current) {
        setLoadingPersonSearch(false);
      }
    }
  }, [isPurchaseDocument]);

  const handleUnifiedSelect = useCallback(async (result: UnifiedSearchResult) => {
    try {
      const token = authService.getTokens()?.access_token;
      const contactResponse = await fetch(`/api/v1/contacts/${result.contact_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (contactResponse.ok) {
        const contactData = await contactResponse.json();
        if (contactData.success && contactData.data) {
          const contact = contactData.data;
          activeContactIdRef.current = contact.id;
          const mappedContact: Contact = {
            id: contact.id,
            contact_type: contact.contact_type,
            display_name: contact.display_name || contact.company_name || contact.contact_person,
            company_name: contact.company_name,
            contact_person: contact.contact_person,
            email: contact.email,
            phone: contact.phone,
            mobile: contact.mobile,
            is_active: contact.is_active,
            payment_terms_days: contact.payment_terms_days,
            credit_limit: contact.credit_limit,
            currency: contact.currency,
            current_balance: contact.current_balance,
            account_status: contact.account_status,
          };
          setSelectedContact(mappedContact);

          await Promise.all([
            loadLocationsForContact(contact.id),
            loadContactPeopleForContact(contact.id)
          ]);

          setFormData(prev => ({
            ...prev,
            contact_id: contact.id,
            contact_name: contact.company_name || contact.display_name || contact.contact_person || '',
            contact_email: contact.email,
            payment_terms: prev.payment_terms ?? contact.payment_terms_days ?? prev.payment_terms,
            location_id: result.location_id || undefined,
            location_name: result.location_name || undefined,
            location_address: result.location_address || undefined,
            contact_person_id: result.person_id || undefined,
            contact_person_name: result.person_name || undefined,
          }));

          if (result.location_id && result.location_name) {
            setSelectedLocation({
              id: result.location_id,
              contact_id: result.contact_id,
              location_name: result.location_name,
              address_line1: result.location_address || undefined,
              city: result.location_city || undefined,
            });
          }

          if (result.person_id && result.person_name) {
            const nameParts = result.person_name.split(' ');
            setSelectedContactPerson({
              id: result.person_id,
              contact_id: result.contact_id,
              first_name: nameParts[0] || '',
              last_name: nameParts.slice(1).join(' ') || '',
              job_title: result.person_job_title || undefined,
              email: result.person_email || undefined,
              phone: result.person_phone || undefined,
            });
          }
        }
      }
    } catch (error) {
      console.error('Error selecting unified search result:', error);
    }
  }, [loadLocationsForContact, loadContactPeopleForContact, setFormData]);

  const handleContactSelect = useCallback((contact: ContactSearchResult | null) => {
    setShowAddLocation(false);
    setShowAddPerson(false);

    if (contact) {
      const mappedContact: Contact = {
        id: contact.id,
        contact_type: contact.contact_type,
        display_name: contact.display_name,
        company_name: contact.company_name,
        contact_person: contact.contact_person,
        contact_number: contact.contact_number,
        email: contact.email,
        phone: contact.phone,
        mobile: contact.mobile,
        is_active: contact.is_active,
        payment_terms_days: contact.payment_terms_days,
        credit_limit: contact.credit_limit,
        currency: contact.currency,
        current_balance: contact.current_balance,
        account_status: contact.account_status,
      };
      setSelectedContact(mappedContact);
      activeContactIdRef.current = contact.id;

      setFormData(prev => ({
        ...prev,
        contact_id: contact.id,
        contact_name: contact.company_name || contact.display_name || contact.contact_person || '',
        contact_email: contact.email,
        payment_terms: prev.payment_terms ?? contact.payment_terms_days ?? prev.payment_terms,
        location_id: undefined,
        location_name: undefined,
        location_address: undefined,
        contact_person_id: undefined,
        contact_person_name: undefined,
      }));

      loadLocationsForContact(contact.id);
      loadContactPeopleForContact(contact.id);

      setSelectedLocation(null);
      setSelectedWarehouse(null);
      setSelectedContactPerson(null);
      setHighlightedContactIndex(-1);

      setTimeout(() => {
        insertButtonRef.current?.focus();
      }, 50);
    } else {
      setSelectedContact(null);
      activeContactIdRef.current = null;
      setLocations([]);
      setContactPeople([]);
      setSelectedLocation(null);
      setSelectedWarehouse(null);
      setSelectedContactPerson(null);
      setFormData(prev => ({
        ...prev,
        contact_id: undefined,
        contact_name: '',
        contact_email: '',
        location_id: undefined,
        location_name: undefined,
        location_address: undefined,
        contact_person_id: undefined,
        contact_person_name: undefined,
        warehouse_id: undefined,
        warehouse_name: undefined,
      }));
    }
  }, [insertButtonRef, loadContactPeopleForContact, loadLocationsForContact, setFormData]);

  const handleLocationSelect = useCallback((location: LocationResult | null) => {
    setSelectedLocation(location);
    if (location) {
      setFormData(prev => ({
        ...prev,
        location_id: location.id,
        location_name: location.location_name,
        location_address: [location.address_line1, location.city, location.postal_code].filter(Boolean).join(', '),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        location_id: undefined,
        location_name: undefined,
        location_address: undefined,
      }));
    }
  }, [setFormData]);

  const handleWarehouseSelect = useCallback((warehouse: LocationResult | null) => {
    setSelectedWarehouse(warehouse);
    if (warehouse) {
      setFormData(prev => ({
        ...prev,
        warehouse_id: warehouse.id,
        warehouse_name: warehouse.location_name,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        warehouse_id: undefined,
        warehouse_name: undefined,
      }));
    }
  }, [setFormData]);

  const handleContactPersonSelect = useCallback((person: ContactPerson | null) => {
    setSelectedContactPerson(person);
    if (person) {
      setFormData(prev => ({
        ...prev,
        contact_person_id: person.id,
        contact_person_name: `${person.first_name} ${person.last_name}`,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        contact_person_id: undefined,
        contact_person_name: undefined,
      }));
    }
  }, [setFormData]);

  const handleSaveNewLocation = useCallback(async () => {
    if (!formData.contact_id) return;
    if (!newLocation.location_name.trim()) { notify.error('Location name is required'); return; }
    setSavingLocation(true);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/contacts/${formData.contact_id}/locations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newLocation)
      });
      if (response.ok) {
        const result = await response.json();
        notify.success('Location created');
        setShowAddLocation(false);
        setNewLocation({ location_name: '', location_type: 'office', address_line1: '', city: '', state: '', postal_code: '', country: 'South Africa', is_primary: false });
        if (result.data) {
          setLocations(prev => [...prev, result.data]);
          handleLocationSelect(result.data);
        }
      } else {
        const data = await response.json();
        notify.error(data.error || 'Failed to create location');
      }
    } catch (error) {
      console.error('Error creating location:', error);
      notify.error('Failed to create location');
    } finally {
      setSavingLocation(false);
    }
  }, [formData.contact_id, handleLocationSelect, newLocation]);

  const handleSaveNewPerson = useCallback(async () => {
    if (!formData.contact_id) return;
    if (!newPerson.first_name.trim() || !newPerson.last_name.trim()) { notify.error('First name and last name are required'); return; }
    setSavingPerson(true);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/contacts/${formData.contact_id}/people`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPerson)
      });
      if (response.ok) {
        const result = await response.json();
        notify.success('Contact person created');
        setShowAddPerson(false);
        setNewPerson({ first_name: '', last_name: '', email: '', phone: '', mobile: '', job_title: '', is_primary: false });
        if (result.data) {
          setContactPeople(prev => [...prev, result.data]);
          handleContactPersonSelect(result.data);
        }
      } else {
        const data = await response.json();
        notify.error(data.error || 'Failed to create person');
      }
    } catch (error) {
      console.error('Error creating person:', error);
      notify.error('Failed to create person');
    } finally {
      setSavingPerson(false);
    }
  }, [formData.contact_id, handleContactPersonSelect, newPerson]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Load contacts on open
  useEffect(() => {
    if (!isOpen) return;

    const loadContactsWithLoading = async () => {
      setLoadingContacts(true);
      try {
        const contactType = isPurchaseDocument ? 'supplier' : 'customer';
        await loadContacts({ contact_type: contactType, pageSize: 500 });
      } catch (error) {
        // Fail gracefully — the contact picker still works manually,
        // and existing contact_id from the document is hydrated separately.
        console.warn('[useContactSearch] Failed to load contacts list:', error);
      } finally {
        setLoadingContacts(false);
      }
    };
    loadContactsWithLoading();
  }, [isOpen, isPurchaseDocument, loadContacts]);

  // Location search effect (debounced)
  useEffect(() => {
    const normalizedSearch = locationSearch.trim();

    if (contactSearchMode === 'location' && normalizedSearch.length >= 2) {
      const timer = setTimeout(() => {
        performLocationSearch(normalizedSearch);
      }, 300);
      return () => clearTimeout(timer);
    } else if (contactSearchMode === 'location') {
      latestLocationSearchRequestId.current += 1;
      setLocationSearchResults([]);
      setLoadingLocationSearch(false);
    }
  }, [locationSearch, contactSearchMode, performLocationSearch]);

  // Person search effect (debounced)
  useEffect(() => {
    const normalizedSearch = personSearch.trim();

    if (contactSearchMode === 'person' && normalizedSearch.length >= 2) {
      const timer = setTimeout(() => {
        performPersonSearch(normalizedSearch);
      }, 300);
      return () => clearTimeout(timer);
    } else if (contactSearchMode === 'person') {
      latestPersonSearchRequestId.current += 1;
      setPersonSearchResults([]);
      setLoadingPersonSearch(false);
    }
  }, [personSearch, contactSearchMode, performPersonSearch]);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (contactDropdownRef.current && !contactDropdownRef.current.contains(e.target as Node)) {
        setIsContactDropdownOpen(false);
      }
      if (locationSearchRef.current && !locationSearchRef.current.contains(e.target as Node)) {
        setIsLocationSearchOpen(false);
      }
      if (personSearchRef.current && !personSearchRef.current.contains(e.target as Node)) {
        setIsPersonSearchOpen(false);
      }
    };
    window.document.addEventListener('mousedown', handleClickOutside);
    return () => window.document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Hydrate contact from formData.contact_id (edit mode)
  useEffect(() => {
    const normalizedId = Number(formData.contact_id ?? null);
    if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
      return;
    }

    if (loadingContacts) {
      return;
    }

    if (Number(selectedContact?.id) === normalizedId) {
      return;
    }

    const contactFromList = contacts.find((c) => Number(c?.id) === normalizedId);
    if (contactFromList?.id != null) {
      setSelectedContact(contactFromList);
      void loadLocationsForContact(normalizedId);
      void loadContactPeopleForContact(normalizedId);
      return;
    }

    let cancelled = false;
    const requestId = ++contactHydrateFromIdRequestRef.current;

    void (async () => {
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) return;

        const contactResponse = await fetch(`/api/v1/contacts/${normalizedId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelled || requestId !== contactHydrateFromIdRequestRef.current) return;
        if (!contactResponse.ok) return;

        const contactData = await contactResponse.json();
        if (
          cancelled
          || requestId !== contactHydrateFromIdRequestRef.current
          || !contactData.success
          || !contactData.data
        ) {
          return;
        }

        const contact = contactData.data;
        const mappedContact: Contact = {
          id: contact.id,
          contact_type: contact.contact_type,
          display_name: contact.display_name || contact.company_name || contact.contact_person,
          company_name: contact.company_name,
          contact_person: contact.contact_person,
          email: contact.email,
          phone: contact.phone,
          mobile: contact.mobile,
          is_active: contact.is_active ?? true,
          payment_terms_days: contact.payment_terms_days,
          credit_limit: contact.credit_limit,
          currency: contact.currency,
          current_balance: contact.current_balance,
          account_status: contact.account_status,
          tax_rate_id: contact.tax_rate_id,
        };
        setSelectedContact(mappedContact);
        await Promise.all([
          loadLocationsForContact(Number(contact.id)),
          loadContactPeopleForContact(Number(contact.id)),
        ]);
      } catch {
        /* ignore — contact picker still works manually */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    formData.contact_id,
    contacts,
    loadingContacts,
    selectedContact?.id,
    loadLocationsForContact,
    loadContactPeopleForContact,
  ]);

  // Restore selectedLocation from formData after locations load (edit mode)
  useEffect(() => {
    if (formData.location_id && locations.length > 0 && !selectedLocation) {
      const loc = locations.find(l => l.id === formData.location_id);
      if (loc) {
        setSelectedLocation(loc);
      }
    }
  }, [formData.location_id, locations, selectedLocation]);

  // Restore selectedWarehouse from formData after locations load (edit mode)
  useEffect(() => {
    if (formData.warehouse_id && locations.length > 0 && !selectedWarehouse) {
      const warehouse = locations.find(l => l.id === formData.warehouse_id && l.location_type === 'warehouse');
      if (warehouse) {
        setSelectedWarehouse(warehouse);
      }
    }
  }, [formData.warehouse_id, locations, selectedWarehouse]);

  // Restore/clear selectedContactPerson from formData after people load (edit mode)
  useEffect(() => {
    if (loadingContactPeople) {
      return;
    }

    const normalizedPersonId = Number(formData.contact_person_id ?? null);
    const hasSavedPerson = Number.isFinite(normalizedPersonId) && normalizedPersonId > 0;

    if (hasSavedPerson && !contactPeople.some((person) => Number(person.id) === normalizedPersonId)) {
      setSelectedContactPerson(null);
      setFormData((prev) => ({
        ...prev,
        contact_person_id: undefined,
        contact_person_name: undefined,
      }));
      return;
    }

    if (hasSavedPerson && !selectedContactPerson) {
      const person = contactPeople.find(p => p.id === normalizedPersonId);
      if (person) {
        setSelectedContactPerson(person);
      }
    }

    if (selectedContactPerson && !contactPeople.some((person) => Number(person.id) === Number(selectedContactPerson.id))) {
      setSelectedContactPerson(null);
    }
  }, [contactPeople, formData.contact_person_id, loadingContactPeople, selectedContactPerson, setFormData]);

  // Restore/clear selectedLocation from formData after locations load (edit mode)
  useEffect(() => {
    if (loadingLocations) {
      return;
    }

    const normalizedLocationId = Number(formData.location_id ?? null);
    const hasSavedLocation = Number.isFinite(normalizedLocationId) && normalizedLocationId > 0;

    if (hasSavedLocation && !locations.some((location) => Number(location.id) === normalizedLocationId)) {
      setSelectedLocation(null);
      setFormData((prev) => ({
        ...prev,
        location_id: undefined,
        location_name: undefined,
        location_address: undefined,
      }));
      return;
    }

    if (hasSavedLocation && !selectedLocation) {
      const location = locations.find(l => l.id === normalizedLocationId);
      if (location) {
        setSelectedLocation(location);
      }
    }

    if (selectedLocation && !locations.some((location) => Number(location.id) === Number(selectedLocation.id))) {
      setSelectedLocation(null);
    }
  }, [formData.location_id, loadingLocations, locations, selectedLocation, setFormData]);

  // Restore/clear selectedWarehouse from formData after locations load (edit mode)
  useEffect(() => {
    if (loadingLocations) {
      return;
    }

    const normalizedWarehouseId = Number(formData.warehouse_id ?? null);
    const hasSavedWarehouse = Number.isFinite(normalizedWarehouseId) && normalizedWarehouseId > 0;

    if (hasSavedWarehouse && !locations.some((location) => Number(location.id) === normalizedWarehouseId)) {
      setSelectedWarehouse(null);
      setFormData((prev) => ({
        ...prev,
        warehouse_id: undefined,
        warehouse_name: undefined,
      }));
      return;
    }

    if (selectedWarehouse && !locations.some((location) => Number(location.id) === Number(selectedWarehouse.id))) {
      setSelectedWarehouse(null);
    }
  }, [formData.warehouse_id, loadingLocations, locations, selectedWarehouse, setFormData]);

  const resetContactSearchState = useCallback(() => {
    setSelectedContact(null);
    setLocations([]);
    setContactPeople([]);
    setSelectedLocation(null);
    setSelectedWarehouse(null);
    setSelectedContactPerson(null);
    setContactSearch('');
    setLocationSearch('');
    setPersonSearch('');
    setLocationSearchResults([]);
    setPersonSearchResults([]);
    setIsContactDropdownOpen(false);
    setIsLocationSearchOpen(false);
    setIsPersonSearchOpen(false);
    setShowAddLocation(false);
    setShowAddPerson(false);
    setHighlightedContactIndex(-1);
    activeContactIdRef.current = null;
  }, []);

  return {
    contacts,
    loadContacts,
    selectedContact,
    setSelectedContact,
    locations,
    contactPeople,
    selectedLocation,
    selectedContactPerson,
    selectedWarehouse,
    loadingLocations,
    loadingContactPeople,
    loadingContacts,
    contactSearchMode,
    setContactSearchMode,
    contactSearch,
    setContactSearch,
    locationSearch,
    setLocationSearch,
    personSearch,
    setPersonSearch,
    locationSearchResults,
    personSearchResults,
    loadingLocationSearch,
    loadingPersonSearch,
    isContactDropdownOpen,
    setIsContactDropdownOpen,
    showStakeholderModal,
    setShowStakeholderModal,
    isLocationSearchOpen,
    setIsLocationSearchOpen,
    isPersonSearchOpen,
    setIsPersonSearchOpen,
    highlightedContactIndex,
    setHighlightedContactIndex,
    showAddLocation,
    setShowAddLocation,
    showAddPerson,
    setShowAddPerson,
    savingLocation,
    savingPerson,
    newLocation,
    setNewLocation,
    newPerson,
    setNewPerson,
    contactDropdownRef,
    locationSearchRef,
    personSearchRef,
    contactSearchInputRef,
    activeContactIdRef,
    loadLocationsForContact,
    loadContactPeopleForContact,
    performLocationSearch,
    performPersonSearch,
    handleUnifiedSelect,
    handleContactSelect,
    handleLocationSelect,
    handleWarehouseSelect,
    handleContactPersonSelect,
    handleSaveNewLocation,
    handleSaveNewPerson,
    resetContactSearchState,
  };
}
